<script setup lang="ts">
import type { DialogContentEmits, DialogContentProps } from "reka-ui"
import type { HTMLAttributes, StyleValue } from "vue"
import { X } from "@lucide/vue"
import {
  DialogContent,
  DialogPortal,
  useForwardPropsEmits,
} from "reka-ui"
import { computed } from "vue"
import { cn } from "@/shared/lib/utils"
import SheetClose from "./SheetClose.vue"
import { type SheetSide, sheetVariants } from "./sheet-variants"
import SheetOverlay from "./SheetOverlay.vue"

/**
 * Контент панелі. Reka бере на себе focus-trap, повернення фокуса на тригер,
 * Esc-close, scroll-lock тіла та `role="dialog"`. `side="bottom"` (дефолт) —
 * мобільний bottom-sheet із drag-handle; `side="right"` — slide-over для ширших
 * екранів. Заголовок (`SheetTitle`) обовʼязковий для скрін-рідерів — передається
 * у слот (за потреби сховати візуально — обгорнути у visually-hidden).
 */
const props = withDefaults(
  defineProps<
    DialogContentProps & {
      class?: HTMLAttributes["class"]
      /** Клас для scrim-overlay (рідко потрібно перевизначати). */
      overlayClass?: HTMLAttributes["class"]
      /** Сторона появи панелі. Дефолт — `bottom` (мобільний кейс). */
      side?: SheetSide
      /** Показувати кнопку-хрестик (top-right). Дефолт — true. */
      showClose?: boolean
      /**
       * Доступна назва кнопки закриття (i18n — на боці споживача). Нейтральний
       * англ. fallback гарантує a11y, навіть якщо споживач забув передати.
       */
      closeAriaLabel?: string
      /**
       * Inline style, прокидується до кореневого DOM-вузла DialogContent
       * (default fallthrough іде до DialogPortal, а не до sheet-content DOM).
       */
      style?: StyleValue
    }
  >(),
  { side: "bottom", showClose: true, closeAriaLabel: "Close" },
)
const emits = defineEmits<DialogContentEmits>()

const delegatedProps = computed(() => {
  const {
    class: _class,
    overlayClass: _overlayClass,
    side: _side,
    showClose: _showClose,
    closeAriaLabel: _closeAriaLabel,
    style: _style,
    ...rest
  } = props
  return rest
})
const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <DialogPortal>
    <SheetOverlay :class="props.overlayClass" />
    <DialogContent
      data-slot="sheet-content"
      :data-side="props.side"
      v-bind="forwarded"
      :class="cn(sheetVariants({ side: props.side }), props.class)"
      :style="props.style"
    >
      <!-- drag-handle: афорданс «потягни вниз», лише для bottom-листа -->
      <div
        v-if="props.side === 'bottom'"
        data-slot="sheet-handle"
        aria-hidden="true"
        class="bg-muted-foreground/30 mx-auto h-1.5 w-12 shrink-0 rounded-full"
      />

      <slot />

      <SheetClose
        v-if="props.showClose"
        :aria-label="props.closeAriaLabel"
        class="ring-offset-background focus-visible:ring-ring text-muted-foreground hover:text-foreground absolute top-4 right-4 inline-flex size-8 items-center justify-center rounded-md opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none"
      >
        <slot name="close">
          <X class="size-4" />
        </slot>
      </SheetClose>
    </DialogContent>
  </DialogPortal>
</template>

<style scoped>
/*
 * Slide-анімація панелі через reka data-state (+ data-side для напрямку).
 * Tailwind-animate плагіна в проєкті нема — keyframes локальні в примітиві
 * (без залежності, без правки globals). Presence reka тримає вузол під час
 * exit, поки програється `closed`-анімація.
 */
[data-slot="sheet-content"][data-side="bottom"][data-state="open"] {
  animation: sheet-slide-up-in 320ms cubic-bezier(0.32, 0.72, 0, 1);
}

[data-slot="sheet-content"][data-side="bottom"][data-state="closed"] {
  animation: sheet-slide-up-out 280ms cubic-bezier(0.32, 0.72, 0, 1);
}

[data-slot="sheet-content"][data-side="right"][data-state="open"] {
  animation: sheet-slide-right-in 320ms cubic-bezier(0.32, 0.72, 0, 1);
}

[data-slot="sheet-content"][data-side="right"][data-state="closed"] {
  animation: sheet-slide-right-out 280ms cubic-bezier(0.32, 0.72, 0, 1);
}

@keyframes sheet-slide-up-in {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes sheet-slide-up-out {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
  }
}

@keyframes sheet-slide-right-in {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

@keyframes sheet-slide-right-out {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
}

@media (prefers-reduced-motion: reduce) {
  [data-slot="sheet-content"][data-state="open"],
  [data-slot="sheet-content"][data-state="closed"] {
    animation-duration: 1ms;
  }
}
</style>
